import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Space, Input, Select, Switch } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatClntWhInventory, useGetMatClntWarehouses, useGetVwMatClntRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { VwMatClntWhInventory, MatWarehouses, VwMatClntRequestItemOutstanding } from '../../../types';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatClntInventory() {
  const navigate = useNavigate();
  const { data: invData = [], isLoading } = useGetMatClntWhInventory() as any;
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const { data: outData = [], refetch: refetchOutstanding } = useGetVwMatClntRequestItemOutstanding() as any;

  const inventory: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const outstandingRows: VwMatClntRequestItemOutstanding[] = Array.isArray(outData) ? outData as any : (outData?.data ?? []);

  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(Number((w as any).id), String((w as any).name)));
    return m;
  }, [warehouses]);

  // Sum outstanding by (itemcode, requested_wh_id) to align with per-warehouse inventory rows
  const outstandingByItemWh = useMemo(() => {
    const m = new Map<string, number>();
    (outstandingRows || []).forEach((r: any) => {
      const wh = r.requested_wh_id;
      const code = String(r.itemcode || '').trim();
      if (!code || wh == null) return; // only count when a requested warehouse is specified
      const key = `${code}-${wh}`;
      const prev = m.get(key) || 0;
      m.set(key, prev + Number(r.outstanding_qty || 0));
    });
    return m;
  }, [outstandingRows]);

  // Filters
  const [warehouseId, setWarehouseId] = useState<number | undefined>(undefined);
  const [query, setQuery] = useState<string>('');
  const [showOutstanding, setShowOutstanding] = useState<boolean>(true);

  const filtered = useMemo(() => {
    const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();
    return (inventory || []).filter((r: any) => {
      if (warehouseId != null && Number(r.warehouse_id) !== Number(warehouseId)) return false;
      if (query) {
        const s = norm(query);
        const idMatch = String(r.itemcode || '').toLowerCase().includes(s);
        const descMatch = norm((r as any).description).includes(s);
        const sizeMatch = norm((r as any).size_text).includes(s);
        if (!idMatch && !descMatch && !sizeMatch) return false;
      }
      return true;
    });
  }, [inventory, warehouseId, query]);

  const columns: ColumnsType<any> = useMemo(() => [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 120, sorter: (a: any, b: any) => String(a.itemcode || '').localeCompare(String(b.itemcode || '')) },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 360, ellipsis: true, sorter: (a: any, b: any) => String(a.description || '').localeCompare(String(b.description || '')) },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 70, sorter: (a: any, b: any) => String(a.size_text || '').localeCompare(String(b.size_text || '')) },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70, sorter: (a: any, b: any) => String(a.unit || '').localeCompare(String(b.unit || '')) },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 220, render: (v: number) => whMap.get(Number(v)) || v, sorter: (a: any, b: any) => (whMap.get(Number(a.warehouse_id)) || '').localeCompare(whMap.get(Number(b.warehouse_id)) || '') },
    { title: 'On-hand Qty', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 120, align: 'right', render: (v: unknown) => formatNumberSmart(v), sorter: (a: any, b: any) => (Number(a.onhand_qty) || 0) - (Number(b.onhand_qty) || 0) },
    {
      title: showOutstanding ? 'Outstanding Req.' : 'Total Requested',
      key: 'requests_metric',
      width: 180,
      align: 'right',
      render: (_: any, r: any) => {
        if (showOutstanding) {
          const k = `${String(r.itemcode)}-${Number(r.warehouse_id)}`;
          return formatNumberSmart(outstandingByItemWh.get(k) || 0);
        }
        return formatNumberSmart((r as any).requests ?? 0);
      },
      exportValue: (r: any) => {
        if (showOutstanding) {
          const k = `${String(r.itemcode)}-${Number(r.warehouse_id)}`;
          return outstandingByItemWh.get(k) || 0;
        }
        return (r as any).requests ?? 0;
      },
      sorter: (a: any, b: any) => {
        const getOutstanding = (x: any) => outstandingByItemWh.get(`${String(x.itemcode)}-${Number(x.warehouse_id)}`) || 0;
        const av = showOutstanding ? getOutstanding(a) : ((a as any).requests ?? 0);
        const bv = showOutstanding ? getOutstanding(b) : ((b as any).requests ?? 0);
        return Number(av) - Number(bv);
      }
    },
  ], [whMap, outstandingByItemWh, showOutstanding]);

  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: Number((w as any).id), label: String((w as any).name) })), [warehouses]);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Client Warehouse Inventory</Title>
              <Text type="secondary">On-hand per Item and Warehouse</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Warehouse"
              allowClear
              value={warehouseId}
              onChange={(v) => setWarehouseId(v)}
              options={whOptions}
              style={{ width: 240 }}
            />
            <Input
              placeholder="Item / Description / Size"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 260 }}
            />
            <Button onClick={() => { setWarehouseId(undefined); setQuery(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space>
            <Text strong>Show Outstanding Requests</Text>
            <Switch checked={showOutstanding} onChange={setShowOutstanding} />
          </Space>
        </Col>
      </Row>
      <StandardTable<VwMatClntWhInventory>
        columns={columns as any}
        dataSource={filtered as any}
        rowKey={(r: any) => `${String((r as any).itemcode)}-${Number((r as any).warehouse_id)}`}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
